<template>
	<modal
		ref="modal"
		title="Checkout"
		:disable-close="loading"
		@close="close">
		<template #body>
			<template v-if="step === 1">
				<BOverlay :show="submitting">
					<template #overlay>
						Your payment is processing. Please do not close this window.
					</template>
					<table class="table">
						<thead>
							<tr>
								<th>Fee</th>
								<th>Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="fee in feesToPay"
								:key="fee.id">
								<td>
									{{ fee.title }}
									<br />
									<i>Completed: {{ moment(fee.service_date).format('DD/MM/Y') }}</i>
								</td>
								<td>${{ fee.amount }}</td>
							</tr>
							<tr>
								<td>
									Insurance Levy
								</td>
								<td>
									${{ insurance_levy }}
								</td>
							</tr>
							<tr>
								<td><label>Total</label></td>
								<td>
									<small>(${{ processing_fees.toFixed(2) }} processing fees.)</small>
									<br>
									${{ total.toFixed(2) }}
								</td>
							</tr>
						</tbody>
					</table>
					<hr>
					<BRow v-if="user.stripe_card_id">
						<BCol>
							<div
								class="d-flex flex-1 btn-group-toggle btn-group bv-no-focus-ring"
								role="radiogroup"
								aria-label="Credit card payment option">
								<label
									class="flex-1 btn btn-white mb-0 rounded-left-05"
									:class="{ active: use_saved_card === true }"
									for="fee-use-saved-card">
									<input
										id="fee-use-saved-card"
										v-model="use_saved_card"
										type="radio"
										name="fee-credit-card-option"
										:value="true"
										autocomplete="off">
									Use Saved Card
								</label>
								<label
									class="flex-1 btn btn-white mb-0 rounded-right-05"
									:class="{ active: use_saved_card === false }"
									for="fee-use-different-card">
									<input
										id="fee-use-different-card"
										v-model="use_saved_card"
										type="radio"
										name="fee-credit-card-option"
										:value="false"
										autocomplete="off">
									Enter a Different Card
								</label>
							</div>
						</BCol>
					</BRow>
					<template v-if="use_saved_card">
						<BRow class="mt-4">
							<BCol>
								<h5>Using Your Saved Card</h5>
								<div class="my-3"></div>
								<BRow>
									<BCol lg="6">
										<BCard bg-variant="light" class="border border-secondary">
											<credit-card :brand="user.stripe_card_brand" :last-four="user.stripe_card_last4" />
										</BCard>
									</BCol>
								</BRow>
							</BCol>
						</BRow>
					</template>
					<template v-else>
						<BRow class="mt-4">
							<BCol>
								<h5>Please enter your card details below.</h5>
								<div class="my-3"></div>
								<credit-card-form
									ref="creditCardForm"
									:country="user.country" />
							</BCol>
						</BRow>
						<BRow class="mt-2">
							<BCol>
								<div class="form-check">
									<input
										id="defaultCheck1"
										class="form-check-input"
										type="checkbox"
										value=""
										:checked="save_card"
										@change="save_card = $event.target.checked">
									<label
										class="form-check-label"
										for="defaultCheck1">
										Save card for future payments
										<span class="text-muted">(this will replace your current saved card)</span>
									</label>
								</div>
							</BCol>
						</BRow>
					</template>
					<div class="row mt-2">
						<div class="col">
							<div
								v-if="payment_errors.length"
								class="alert alert-danger mb-0">
								<p
									v-for="error in payment_errors"
									:key="error"
									class="mb-0">
									{{ error }}
								</p>
							</div>
						</div>
					</div>
				</BOverlay>
			</template>
			<template v-else-if="step === 2">
				<div class="text-center">
					<h1>Success</h1>
					<p>We've received your payment! A confirmation has also been sent to your email address.</p>
					<img
						src="/assets/img/undraw_order_confirmed_1m3v.svg"
						alt="Payment Success Image"
						height="200px" />
				</div>
			</template>
		</template>
		<template #footer>
			<template v-if="step === 1">
				<button
					class="btn btn-md btn-primary"
					:disabled="loading || submitting"
					@click="submit">
					<BSpinner v-if="submitting" small
						class="mr-2" />
					Make Payment
				</button>
			</template>
		</template>
	</modal>
</template>

<script>
	import CreditCard from '@js/components/my/ui/CreditCard.vue';
	import CreditCardForm from '@js/components/my/client/CreditCardForm.vue';
	import Modal from '@js/components/Modal.vue';
	import { mapGetters } from 'vuex';
	import { v4 as uuid } from 'uuid';

	export default {
		components: {
			CreditCard,
			CreditCardForm,
			Modal,
		},
		data() {
			return {
				step: 1,
				fees: [],
				insurance_levy: null,
				complete: false,
				success: false,
				loading: false,
				submitting: false,
				use_saved_card: false,
				save_card: false,
				processing_fees: 0,
				total: 0,
				stripeKey: null,
				payment_errors: [],
				idempotency_key: uuid(),
				active_payment_request: false,
			}
		},
		computed: {
			...mapGetters({
				user: 'auth/user',
			}),
			totalSelectedAmount() {
				let total = 0;

				this.feesToPay.forEach(fee => {
					total += parseFloat(fee.amount);
				});

				return total.toFixed(2);
			},
			feesToPay() {
				return this.fees.filter((fee) => {
					return fee.action === 'Pay'
				});
			}
		},
		mounted() {
			this.$eventBus.$on('feeCheckoutModal.open', (fees) => {
				this.step = 1;
				this.fees = fees;
				this.use_saved_card = this.user.stripe_card_last_four ? true : false;
				this.fetchInsuranceLevy().then(() => {
					this.processing_fees = 0.00;
					this.total = parseFloat(this.totalSelectedAmount) + parseFloat(this.insurance_levy);
					this.$refs.modal.open();
				})
			});
		},
		beforeUnmount() {
			this.$eventBus.$off('feeCheckoutModal.open');
		},
		methods: {
			async fetchInsuranceLevy() {
				this.loading = true;
				return this.$axios.get('/api/insurance-levy', {
					params: {
						fee_ids: this.feesToPay.map(item => {
							return item.id
						}),
						user_id: this.user.id
					}
				}).then(response => {
					this.insurance_levy = response.data.toFixed(2)
				}).catch(error => {
					if (error.response && error.response.status === 403) {
						toastr.error(error.response.data.message);
					} else {
						toastr.error('An error occurred fetching the Insurance Levy.');
					}

					throw error;
				}).finally(() => {
					this.loading = false;
				});
			},
			async submit() {
				if (this.active_payment_request) {
					return;
				}

				this.submitting = true;
				this.payment_errors = [];
				this.active_payment_request = true;

				try {
					let token = null;

					if (!this.use_saved_card) {
						token = await this.$refs.creditCardForm.createToken();
					}

					let data = {
						fees: this.feesToPay.map(item => {
							return {
								id: item.id,
								service_date: item.service_date
							};
						}),
						use_saved_card: this.use_saved_card,
						stripe_token: token,
						save_card: this.save_card,
						idempotency_key: this.idempotency_key,
					};

					await this.$axios.post('/api/cleaner/fees', data).then(() => {
						if (this.save_card) {
							this.$store.dispatch('auth/fetchCurrentUser');
						}
						this.step = 2;
						this.fees = [];
						this.processing_fees = 0;
						this.complete = false;
						this.use_saved_card = false;
						this.total = 0;
						this.success = true;
						this.save_card = false;
						this.idempotency_key = uuid();
						this.$eventBus.$emit('fees.paid');
					}).catch((error) => {
						if ([403, 500].includes(error.response.status)) {
							toastr.error('An error occurred');
						}
						if (error.response.status === 400) {
							toastr.error(error.response.data.message);
						}
						if (error.response.status === 422) {
							for (var item in error.response.data.errors) {
								if (Object.prototype.hasOwnProperty.call(error.response.data.errors, item)) {
									this.payment_errors.push(error.response.data.errors[item][0]);
								}
							}
						}
						this.idempotency_key = uuid();
						this.loading = false;
						this.submitting = false;
						this.active_payment_request = false;
					})
				} catch (error) {
					this.$bvToast.toast('An error occurred', {
						title: 'Error',
						variant: 'danger',
						solid: true,
					});

					this.loading = false;
					this.submitting = false;
					this.active_payment_request = false;
				}
			},
			close() {
				this.success = false;
				this.submitting = false;
				this.loading = false;
				this.active_payment_request = false;
			}
		}
	}
</script>

<style scoped>
.stripe-card {
	width: 300px;
	padding-left: 1rem;
}

.stripe-card.complete {
	border-color: green;
}

.card {
	border: none;
}
</style>
